import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	Dialog,
	FormsIcon,
	Link,
	ModuleHeader,
	SpinnerWithBackground,
	Text,
	UploadIcon,
} from '@viapeople/components'
import type React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { ExportFormDialog } from './components/ExportFormDialog'
import { ImportFormDialog } from './components/ImportFormDialog'
import type { ExportedForm } from './helpers/formExportImport'
import type { Form, FormSearchInput } from './types'

interface Column {
	field: keyof Form
	label: string
	render?: (item: NonNullable<Form>) => React.ReactNode
}

export const FormsPage = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const toast = useToast()
	const queryClient = useQueryClient()
	const [selectedItems, setSelectedItems] = useState<Form[]>([])
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
	const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
	const [formToExport, setFormToExport] = useState<Form | null>(null)

	const {
		data: organization,
		isLoading,
		isError,
	} = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const { mutate: deleteFormsMutation } = useMutation({
		...trpc.admin.forms.delete.mutationOptions(),
		onSuccess: () => {
			setSelectedItems([])
			toast.showToast('Forms deleted successfully')
		},
		onError: () => {
			toast.showToast('An error occurred while deleting forms', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.forms.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const { mutate: duplicateFormsMutation } = useMutation({
		...trpc.admin.forms.duplicate.mutationOptions(),
		onSuccess: () => {
			setSelectedItems([])
			toast.showToast('Forms duplicated successfully')
		},
		onError: () => {
			toast.showToast('An error occurred while duplicating forms', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.forms.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleDelete = () => {
		if (!organizationId) return
		if (selectedItems.length === 0) return
		deleteFormsMutation({
			ids: selectedItems.map((item) => item.id),
			organizationId,
		})
		setIsDeleteModalOpen(false)
	}

	const handleDuplicate = () => {
		if (selectedItems.length === 0) return
		duplicateFormsMutation({
			formIds: selectedItems.map((item) => item.id),
		})
	}

	const handleExport = async () => {
		if (selectedItems.length !== 1) {
			toast.showToast('Please select exactly one form to export', 'error')
			return
		}

		const formId = selectedItems[0].id
		try {
			const formData = await queryClient.fetchQuery(
				trpc.admin.forms.getById.queryOptions({
					id: formId,
					organizationId: organizationId as string,
				}),
			)

			if (formData) {
				setFormToExport(formData)
				setIsExportDialogOpen(true)
			}
		} catch {
			toast.showToast('Failed to load form for export', 'error')
		}
	}

	const handleImport = (formData: ExportedForm) => {
		// Navigate to form builder with the imported data
		navigate(`/organizations/${organizationId}/forms/new`, {
			state: { importedForm: formData },
		})
	}

	const handleCreateForm = () => {
		navigate(`/organizations/${organizationId}/forms/new`)
	}

	const handleEditForm = (form: NonNullable<Form>) => {
		navigate(`/organizations/${organizationId}/forms/${form.id}`)
	}

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (!organization) {
		return (
			<main className='p-6'>
				<Text>Organization not found</Text>
				<Link href='/organizations' variant='default'>
					Back to Organizations
				</Link>
			</main>
		)
	}

	if (isError) {
		return (
			<main className='p-6'>
				<Text>Error loading forms</Text>
				<Link href='/organizations' variant='default'>
					Back to Organizations
				</Link>
			</main>
		)
	}

	const columns: Column[] = [
		{
			field: 'name',
			label: 'Name',
		},
		{
			field: 'createdAt',
			label: 'Created At',
			render: (item: NonNullable<Form>) => new Date(item.createdAt).toLocaleDateString(),
		},
	]

	return (
		<main>
			<div className='p-6'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Organizations', to: '/organizations' },
						{ label: organization.name, to: `/organizations/${organizationId}` },
						{ label: 'Forms' },
					]}
					title='Forms'
					logo={{
						url: organization.logo,
						name: organization.name,
					}}
				/>

				<div className='mt-6'>
					<GenericTable<Form, FormSearchInput>
						columns={columns}
						idField='id'
						selectable
						onSelectionChange={setSelectedItems}
						onRowClick={handleEditForm}
						actionButtons={[
							{
								icon: AddIcon,
								iconPosition: 'left',
								children: 'Create Form',
								variant: 'tertiary',
								onClick: handleCreateForm,
							},
							{
								icon: UploadIcon,
								iconPosition: 'left',
								children: 'Import Form',
								variant: 'tertiary',
								onClick: () => setIsImportDialogOpen(true),
							},
						]}
						queryOptionsGenerator={(options) =>
							trpc.admin.forms.search.queryOptions(
								{
									...options,
									organizationId: organizationId as string,
								},
								{ placeholderData: (prev) => prev },
							)
						}
						emptyState={{
							icon: FormsIcon,
							title: 'No forms to display',
							description: 'Get started by creating a form',
							actionLabel: 'Create Form',
							onAction: handleCreateForm,
						}}
					/>
				</div>

				<Dialog
					isOpen={isDeleteModalOpen}
					onClose={() => setIsDeleteModalOpen(false)}
					onConfirm={handleDelete}
					title='Delete Forms'
					message={`You are about to delete ${selectedItems.length} ${
						selectedItems.length === 1 ? 'form' : 'forms'
					} from this organization. This action cannot be reversed.`}
					type='delete'
					confirmLabel='Delete'
				/>

				<Dialog
					isOpen={isDuplicateModalOpen}
					onClose={() => setIsDuplicateModalOpen(false)}
					onConfirm={handleDuplicate}
					title='Duplicate Forms'
					message={`Duplicate ${selectedItems.length} form${selectedItems.length === 1 ? '' : 's'}?`}
					type='confirm'
					confirmLabel='Duplicate'
				/>

				{selectedItems.length > 0 && (
					<ActionBar
						rightActions={[
							{
								label: `Delete ${selectedItems.length > 1 ? `(${selectedItems.length})` : ''}`,
								variant: 'destructive',
								onClick: () => setIsDeleteModalOpen(true),
							},
							{
								label: `Duplicate ${selectedItems.length > 1 ? `(${selectedItems.length})` : ''}`,
								variant: 'primary',
								onClick: () => setIsDuplicateModalOpen(true),
							},
							...(selectedItems.length === 1
								? [
										{
											label: 'Export',
											variant: 'secondary' as const,
											onClick: handleExport,
										},
									]
								: []),
						]}
					/>
				)}

				{formToExport && (
					<ExportFormDialog
						isOpen={isExportDialogOpen}
						onClose={() => {
							setIsExportDialogOpen(false)
							setFormToExport(null)
						}}
						form={formToExport}
					/>
				)}

				<ImportFormDialog
					isOpen={isImportDialogOpen}
					onClose={() => setIsImportDialogOpen(false)}
					onImport={handleImport}
				/>
			</div>
		</main>
	)
}
